import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { orderService, Order, CreateOrderDto } from '../services/api/order.service';
import { getApiErrorMessage } from '../utils/errorHandling';
import type { RootState } from './store';

interface OrdersState {
  orders: Order[];
  currentOrder: Order | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  isLoading: boolean;
  error: string | null;
  lastFetch: number | null;
}

const initialState: OrdersState = {
  orders: [],
  currentOrder: null,
  pagination: null,
  isLoading: false,
  error: null,
  lastFetch: null,
};

/**
 * Async thunk to create a new order
 */
export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData: CreateOrderDto, { rejectWithValue }) => {
    try {
      const order = await orderService.createOrder(orderData);
      return order;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to create order')
      );
    }
  }
);

/**
 * Async thunk to fetch user's orders
 * Usage: dispatch(fetchMyOrders({ page: 1, limit: 20 }))
 */
export const fetchMyOrders = createAsyncThunk(
  'orders/fetchMyOrders',
  async (params?: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const result = await orderService.getMyOrders(params);
      // Handle both array and paginated response
      if (Array.isArray(result)) {
        return { orders: result, pagination: null };
      }
      return result;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch orders')
      );
    }
  }
);

/**
 * Async thunk to fetch a single order by ID
 */
export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const order = await orderService.getOrderById(orderId);
      return order;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch order')
      );
    }
  }
);

/**
 * Async thunk to fetch a single order by order number
 */
export const fetchOrderByNumber = createAsyncThunk(
  'orders/fetchOrderByNumber',
  async (orderNumber: string, { rejectWithValue }) => {
    try {
      const order = await orderService.getOrderByNumber(orderNumber);
      return order;
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch order')
      );
    }
  }
);

/** Guest confirmation: opaque token from URL (never id alone). */
export const fetchGuestOrderById = createAsyncThunk(
  'orders/fetchGuestOrderById',
  async (
    { id, accessToken }: { id: string; accessToken: string },
    { rejectWithValue }
  ) => {
    try {
      return await orderService.getGuestOrder(id, accessToken);
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to fetch order')
      );
    }
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    clearOrdersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create Order
    builder
      .addCase(createOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action: PayloadAction<Order>) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        // Add to orders list if not already there
        const existingIndex = state.orders.findIndex(o => o.id === action.payload.id);
        if (existingIndex === -1) {
          state.orders.unshift(action.payload);
        }
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch My Orders
    builder
      .addCase(fetchMyOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both array and paginated response
        const ordersArray = Array.isArray(action.payload)
          ? action.payload
          : (action.payload as any)?.orders || [];
        state.orders = ordersArray;
        // Store pagination if available
        if (!Array.isArray(action.payload) && (action.payload as any)?.pagination) {
          state.pagination = (action.payload as any).pagination;
        } else {
          state.pagination = null;
        }
        state.lastFetch = Date.now();
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Order By ID
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action: PayloadAction<Order>) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        
        // Update in orders list if exists
        const existingIndex = state.orders.findIndex(o => o.id === action.payload.id);
        if (existingIndex !== -1) {
          state.orders[existingIndex] = action.payload;
        } else {
          state.orders.unshift(action.payload);
        }
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchGuestOrderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGuestOrderById.fulfilled, (state, action: PayloadAction<Order>) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        const existingIndex = state.orders.findIndex((o) => o.id === action.payload.id);
        if (existingIndex !== -1) {
          state.orders[existingIndex] = action.payload;
        } else {
          state.orders.unshift(action.payload);
        }
      })
      .addCase(fetchGuestOrderById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Order By Number
    builder
      .addCase(fetchOrderByNumber.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderByNumber.fulfilled, (state, action: PayloadAction<Order>) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        
        // Update in orders list if exists
        const existingIndex = state.orders.findIndex(o => o.id === action.payload.id);
        if (existingIndex !== -1) {
          state.orders[existingIndex] = action.payload;
        } else {
          state.orders.unshift(action.payload);
        }
      })
      .addCase(fetchOrderByNumber.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const { clearCurrentOrder, clearOrdersError } = ordersSlice.actions;

// Selectors
export const selectOrders = (state: RootState) => state.orders.orders;
export const selectCurrentOrder = (state: RootState) => state.orders.currentOrder;
export const selectOrdersLoading = (state: RootState) => state.orders.isLoading;
export const selectOrdersError = (state: RootState) => state.orders.error;

// Reducer
export default ordersSlice.reducer;






